import { useCallback } from "react";
import { toast, types } from "@vilocnv/allsetra-core";
import { useAppDispatch } from "./reduxHooks";
import { updateObjectRideThunk } from "app/features";
import { useTranslation } from "react-i18next";

interface DetourRouteValues {
  detourKilometers: string;
  detourReason: string;
  changeReason: string;
}

interface PrivateMileageValues {
  privateMileage: string;
  changeReason: string;
}

interface UseRideActionsProps {
  accountId: string;
  onSuccess?: () => void;
}

export const useRideActions = ({
  accountId,
  onSuccess
}: UseRideActionsProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const addDetourRoute = useCallback(
    async (ride: types.IObjectRide, values: DetourRouteValues) => {
      try {
        const isBusinessOrCommute = ride.rideMode === 1 || ride.rideMode === 3;

        const data: any = {
          uniqueId: ride.uniqueId,
          rideMode: ride.rideMode,
          detourMileage: Number(values.detourKilometers),
          detourReason: values.detourReason,
          changeReason: values.changeReason,
          comments: ride.comments || null
        };

        const existingPrivateMileage = (ride as any).privateMileage;
        if (existingPrivateMileage) {
          data.privateMileage = existingPrivateMileage;
        } else if (isBusinessOrCommute) {
          data.privateMileage = 0;
        }

        if (ride.key?.uniqueId) {
          data.keyId = ride.key.uniqueId;
        } else if ((ride as any).driver?.uniqueId) {
          data.driverId = (ride as any).driver.uniqueId;
        }

        const { type } = await dispatch(
          updateObjectRideThunk({
            accountId,
            objectId: ride.object?.uniqueId ?? "",
            rideId: ride.uniqueId,
            data
          })
        );

        if (type === "objects/updateObjectRideThunk/fulfilled") {
          toast.success(t("toasts.detourMileageAddedSuccess"));
          onSuccess?.();
          return { success: true };
        }

        return { success: false };
      } catch (error) {
        console.error("Error adding detour route:", error);
        toast.error("Failed to add detour mileage.");
        return { success: false, error };
      }
    },
    [accountId, dispatch, onSuccess]
  );

  const addPrivateMileage = useCallback(
    async (ride: types.IObjectRide, values: PrivateMileageValues) => {
      try {
        const data: any = {
          uniqueId: ride.uniqueId,
          rideMode: ride.rideMode,
          privateMileage: Number(values.privateMileage),
          changeReason: values.changeReason,
          comments: ride.comments || null,
          detourMileage: (ride as any).detourMileage || null,
          detourReason: (ride as any).detourReason || null
        };

        if (ride.key?.uniqueId) {
          data.keyId = ride.key.uniqueId;
        } else if ((ride as any).driver?.uniqueId) {
          data.driverId = (ride as any).driver.uniqueId;
        }

        const { type } = await dispatch(
          updateObjectRideThunk({
            accountId,
            objectId: ride.object?.uniqueId ?? "",
            rideId: ride.uniqueId,
            data
          })
        );

        if (type === "objects/updateObjectRideThunk/fulfilled") {
          toast.success(t("toasts.privateMileageAddedSuccess"));
          onSuccess?.();
          return { success: true };
        }

        return { success: false };
      } catch (error) {
        console.error("Error adding private mileage:", error);
        toast.error("Failed to add private mileage.");
        return { success: false, error };
      }
    },
    [accountId, dispatch, onSuccess]
  );

  return {
    addDetourRoute,
    addPrivateMileage
  };
};
